import { useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Input, Dropdown } from 'cat-ecommerce-alloy';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from '@app/hooks';
import cx from 'classnames';
import PropTypes from 'prop-types';
import {
  getCountryObjectFromCallingCode,
  validPhoneInputCheck
} from '../../utils';
import { googlePhoneValidate } from '../../googleUtils';
import Field from '@app/components/common/form/Field';
import styles from './PhoneNumberInput.module.scss';
import { replaceTokensInString } from '@app/utils';
import { PAGE_BREAKPOINTS } from '@app/constants/commonConstants';
import { PCC_PUD_CONTACT_UPDATES } from '@app/constants/featureFlags';
import { DrawerDropdown } from '@app/components/common/DrawerDropdown';

const PhoneNumberInput = ({
  dropDownName,
  inputName,
  showRequired: isPhoneRequired,
  handleValueFromSessionStorage = false,
  valuefromSessionStorage = {},
  shouldCheckDirty = true,
  labelClassName = '',
  showMobileDrawerFlag = false
}) => {
  const countryDataSelector = useSelector(state => state.dealer?.countryInfo);
  const contactUpdatesFlagEnabled = useSelector(
    s => s.featureFlag?.[PCC_PUD_CONTACT_UPDATES]
  );
  const isPCCSortPhoneNumberAlphabet = useSelector(
    s => s.featureFlag?.PCCSortPhoneNumberAlphabet
  );
  const countryData = useMemo(
    () =>
      countryDataSelector
        ? countryDataSelector
            .filter(countryObject => !!countryObject.callingCode)
            ?.sort((a, b) => {
              // Only sort if isPCCSortPhoneNumberAlphabet ff is true
              if (isPCCSortPhoneNumberAlphabet) {
                return a?.code.localeCompare(b?.code); // Sort alphabetically by code
              }
              return 0; // Return 0 to keep the order as is
            })
        : [],
    [countryDataSelector]
  );

  const { t } = useTranslation();
  const [selectedCountryObject, setSelectedCountryObject] = useState(null);
  const [valuesLoaded, setValuesLoaded] = useState(false);
  const {
    values,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    resetForm,
    setStatus,
    status,
    dirty,
    touched,
    errors
  } = useFormikContext();
  const inputFieldHasError = errors[inputName];

  const itemToDisplayName = useCallback(
    item => (item && item.code ? `${item.code}` : ''),
    []
  );

  const formatPhoneHandler = useCallback(
    valueToValidate => {
      if (!isPhoneRequired && !values[inputName]) {
        return; //dont validate if phone is optional and is empty
      }

      if (isPhoneRequired && !values[inputName]) {
        return replaceTokensInString(
          t('1626_FIELD_REQUIRED_ERROR'),
          t('CAT_EMAIL_PHONE1')
        );
      }
      const dropDownValidate = typeof valueToValidate !== 'string';
      let filteredPhone;
      if (!dropDownValidate) {
        filteredPhone = valueToValidate.replace(/[^\d +()-]/g, '');
        const phoneInputCheck = validPhoneInputCheck(
          filteredPhone,
          inputName,
          setFieldValue,
          t('ERROR_INVALIDPHONE2')
        );
        if (phoneInputCheck !== true) {
          return phoneInputCheck;
        }
      } else {
        filteredPhone = values[inputName];
      }
      return googlePhoneValidate(
        inputName,
        t('INVALID_PHONE_FORMAT'),
        filteredPhone,
        selectedCountryObject,
        dropDownValidate,
        setFieldValue,
        setFieldError
      );
    },
    [
      inputName,
      selectedCountryObject,
      t,
      values,
      isPhoneRequired,
      setFieldValue,
      setFieldError
    ]
  );
  const dropdownBlurHandler = () => {
    !touched[inputName] &&
      values[inputName] !== '' &&
      setFieldTouched(inputName, true, false);
    formatPhoneHandler();
  };
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const showMobileDrawer = showMobileDrawerFlag && !isMD;
  const contactUpdates = contactUpdatesFlagEnabled && !isMD;
  useEffect(() => {
    if (!shouldCheckDirty && selectedCountryObject) {
      formatPhoneHandler();
    }
  }, [selectedCountryObject, shouldCheckDirty]);
  const dropdownValueCheck = shouldCheckDirty ? dirty : false;
  useEffect(() => {
    //to run after loading for pre-populating
    if (
      values?.[inputName] &&
      values?.[inputName] !== '' &&
      values?.country &&
      values?.country !== '' &&
      !valuesLoaded &&
      !dropdownValueCheck
    ) {
      const extractedCallingCodeArray = values[inputName].match(/\+\d+/); //extract the calling code from the phone
      const extractedCallingCode =
        extractedCallingCodeArray && extractedCallingCodeArray[0];
      const currentPhoneWithoutCountryCode = values[inputName].replace(
        /\+\d+ +/,
        ''
      ); //remove country code
      const currentCountryCallingCodeObject = getCountryObjectFromCallingCode({
        countryData,
        propCallingCode: extractedCallingCode,
        userCountry: values.country
      });
      setSelectedCountryObject(currentCountryCallingCodeObject); // populate the dropdown fron the extracted code
      setFieldValue(
        inputName,
        `${currentCountryCallingCodeObject?.callingCode} ${currentPhoneWithoutCountryCode}` ||
          '',
        false
      );
      setValuesLoaded(true);
      setStatus('pre-loaded');
    } else if (
      ((values?.country !== '' && !values?.[inputName]) ||
        dropdownValueCheck) &&
      !valuesLoaded
    ) {
      // handleValueFromSessionStorage is for prepopulating country code when
      // user has stored values on session storage from PUD page
      const countryObject = handleValueFromSessionStorage
        ? valuefromSessionStorage
        : getCountryObjectFromCallingCode({
            countryData,
            userCountry: values.country
          });
      //for when it doesn't load a phone number
      setSelectedCountryObject(countryObject);
      setValuesLoaded(true);
      setStatus(null);
    }
  }, [
    inputName,
    values,
    countryData,
    setFieldValue,
    setStatus,
    valuesLoaded,
    status,
    dropdownValueCheck
  ]); //pre loading of the form after everything loads correctly

  useEffect(() => {
    //to avoid Formiks dirty functionality when user just loaded the page and the phone number was manipulated by the logic
    if (status === 'pre-loaded') {
      resetForm({ values });
      setStatus(null);
    }
  }, [values, resetForm, setStatus, status]);

  return (
    <div
      className={cx(
        'd-flex flex-grow-1 justify-content-between',
        styles['phone-number-input'],
        {
          'flex-column flex-lg-row align-items-lg-end':
            !contactUpdatesFlagEnabled,
          'gap-1 flex-row align-items-end flex-wrap': contactUpdatesFlagEnabled,
          'mb-3 pb-3': inputFieldHasError,
          [styles['phone-number__capitalize']]:
            labelClassName === 'text-capitalize'
        }
      )}
    >
      {(contactUpdates || showMobileDrawer) && (
        <label
          className={cx(styles['phone-number-label'], {
            'text-capitalize': labelClassName === 'text-capitalize'
          })}
          htmlFor={dropDownName}
        >
          {t('CAT_EMAIL_PHONE1')}
          {isPhoneRequired && '*'}
        </label>
      )}
      {showMobileDrawer ? (
        <DrawerDropdown
          name={dropDownName}
          id={dropDownName}
          itemToString={itemToDisplayName}
          items={countryData ?? []}
          label={t('COUNTRY_CODE')}
          onChange={item => {
            setSelectedCountryObject(item);
          }}
          selected={selectedCountryObject}
          headerVariant="regular"
          orientation="side"
          verticalMaxHeight="large"
          showBackButton
          withSearch
          searchPlaceHolder={t('SEARCH_COUNTRY_CODE')}
          hideFieldLabel
          containerClassName="w-25"
        />
      ) : (
        <Field
          type="dropdown"
          as={Dropdown}
          name={dropDownName}
          id={dropDownName}
          itemToString={itemToDisplayName}
          items={countryData ?? []}
          keyToString={({ code }) => code}
          onChange={item => {
            setSelectedCountryObject(item);
          }}
          labelClassName={labelClassName}
          onBlur={dropdownBlurHandler}
          selected={selectedCountryObject}
          label={!contactUpdates && t('CAT_EMAIL_PHONE1')}
          showRequired={isPhoneRequired}
          showOptional={!isPhoneRequired}
          noValidate
          data-cs-mask
        />
      )}
      <div
        className={cx('flex-grow-1', {
          'w-25': contactUpdatesFlagEnabled
        })}
      >
        <Field
          type="tel"
          as={Input}
          name={inputName}
          id={inputName}
          data-testid="phoneNumberInput"
          validator={formatPhoneHandler}
          formGroupClassname="w-100"
          className={cx({
            [styles['phone-number-field']]: contactUpdatesFlagEnabled
          })}
          data-cs-mask
        />
      </div>
    </div>
  );
};
PhoneNumberInput.propTypes = {
  dropDownName: PropTypes.string,
  inputName: PropTypes.string,
  showRequired: PropTypes.bool,
  handleValueFromSessionStorage: PropTypes.bool,
  labelClassName: PropTypes.string,
  shouldCheckDirty: PropTypes.bool,
  showMobileDrawerFlag: PropTypes.bool
};

export default PhoneNumberInput;
